/**
 * Validation Utilities
 * Handles form validation rules
 */

import { Question, ValidationRule } from '../types/formConfig'

/**
 * Validates an email address
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validates a UK phone number
 */
export const isValidUKPhone = (phone: string): boolean => {
  // Remove spaces, dashes, and parentheses
  const cleaned = phone.replace(/[\s\-()]/g, '')

  // UK phone numbers typically start with 0 or +44 and are 10-11 digits
  const ukPhoneRegex = /^(\+44|0)[0-9]{9,10}$/
  return ukPhoneRegex.test(cleaned)
}

/**
 * Validates a UK postcode
 */
export const isValidUKPostcode = (postcode: string): boolean => {
  // UK postcode regex
  const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i
  return postcodeRegex.test(postcode.trim())
}

/**
 * Validates a single answer against validation rules
 */
export const validateAnswer = (
  question: Question,
  value: string | string[] | number | object | null | undefined
): string | null => {
  // Check required
  if (question.required) {
    if (value === null || value === undefined || value === '') {
      return 'This question is required'
    }

    if (Array.isArray(value) && value.length === 0) {
      return 'Please select at least one option'
    }

    // For address objects, check if they have required fields
    if (typeof value === 'object' && !Array.isArray(value)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Define proper Address type interface
      const addr = value as any
      if (!addr.line1 && !addr.fullAddress) {
        return 'Please provide a valid address'
      }
      return null // Address is valid
    }
  }

  // If no value and not required, validation passes
  if (!value && !question.required) {
    return null
  }

  const stringValue = String(value)

  // Apply custom validation rules
  if (question.validation) {
    for (const rule of question.validation) {
      const error = applyValidationRule(rule, stringValue)
      if (error) {
        return error
      }
    }
  }

  // Apply type-specific validation
  switch (question.questionType) {
    case 'email':
      if (stringValue && !isValidEmail(stringValue)) {
        return 'Please enter a valid email address'
      }
      break

    case 'tel':
      if (stringValue && !isValidUKPhone(stringValue)) {
        return 'Please enter a valid UK phone number'
      }
      break

    case 'postcode':
      if (stringValue && !isValidUKPostcode(stringValue)) {
        return 'Please enter a valid UK postcode'
      }
      break
  }

  return null
}

/**
 * Apply a specific validation rule
 */
const applyValidationRule = (
  rule: ValidationRule,
  value: string
): string | null => {
  switch (rule.type) {
    case 'email':
      if (!isValidEmail(value)) {
        return rule.errorMessage
      }
      break

    case 'tel':
      if (!isValidUKPhone(value)) {
        return rule.errorMessage
      }
      break

    case 'postcode':
      if (!isValidUKPostcode(value)) {
        return rule.errorMessage
      }
      break

    case 'minLength':
      if (rule.value && value.length < Number(rule.value)) {
        return rule.errorMessage
      }
      break

    case 'maxLength':
      if (rule.value && value.length > Number(rule.value)) {
        return rule.errorMessage
      }
      break

    case 'pattern':
      if (rule.value) {
        const regex = new RegExp(String(rule.value))
        if (!regex.test(value)) {
          return rule.errorMessage
        }
      }
      break

    case 'required':
      if (!value || value.trim() === '') {
        return rule.errorMessage
      }
      break
  }

  return null
}
